/**
 * Domain routing helpers for the Venus Glow Haven ↔ NeuroHeal Haven split.
 *
 *   venusglowhaven.com   → marketing umbrella (Library, Journal, Media, About,
 *                          Masterclass, the Venus Glow Haven homepage at `/`)
 *   neurohealhaven.com   → the NeuroHeal app proper (Landing at `/`, Dashboard,
 *                          Sound Healing, Haven AI, sign-in, everything else)
 *
 * The SAME codebase serves both domains. These helpers:
 *  1. `appUrl(path)`     — produce a NeuroHeal app URL (cross-origin on venus,
 *                          same-origin on the app host or preview).
 *  2. `umbrellaUrl(path)`— produce a Venus marketing URL (cross-origin on the
 *                          app host, same-origin on venus or preview).
 *  3. `<DomainRedirect>` — when a visitor lands on the "wrong" host for a
 *                          given route, forward them to the right one.
 *  4. Hostname checks    — `isVenusGlowHost`, `isNeuroHealHost`.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const NEUROHEAL_ORIGIN = 'https://neurohealhaven.com';
const VENUS_ORIGIN = 'https://venusglowhaven.com';

const VENUS_HOSTS = new Set(['venusglowhaven.com', 'www.venusglowhaven.com']);
const NEUROHEAL_HOSTS = new Set(['neurohealhaven.com', 'www.neurohealhaven.com']);

// Paths that belong on the marketing umbrella host (venusglowhaven.com).
const UMBRELLA_PATHS = ['/library', '/media', '/insights', '/about', '/masterclass', '/neuroheal', '/affiliate'];

export const isVenusGlowHost = () => {
  if (typeof window === 'undefined') return false;
  return VENUS_HOSTS.has(window.location.hostname);
};

export const isNeuroHealHost = () => {
  if (typeof window === 'undefined') return false;
  return NEUROHEAL_HOSTS.has(window.location.hostname);
};

const isUmbrellaPath = (pathname) => {
  if (UMBRELLA_PATHS.includes(pathname)) return true;
  if (pathname.startsWith('/insights/')) return true;
  return false;
};

/** Absolute or relative URL for an app route. */
export const appUrl = (path) => {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (isVenusGlowHost()) return `${NEUROHEAL_ORIGIN}${p}`;
  return p;
};

/** Hard-navigate to the app (used for "Experience NeuroHeal" CTAs etc). */
export const goToApp = (path) => {
  if (typeof window !== 'undefined') window.location.assign(appUrl(path));
};

/** Absolute or relative URL for a marketing umbrella route. */
export const umbrellaUrl = (path) => {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (isNeuroHealHost()) return `${VENUS_ORIGIN}${p}`;
  return p;
};

/**
 * Router-level guard. Mount once at the top of the React tree.
 *
 * - On venusglowhaven.com: any path that's NOT an umbrella route (and not the
 *   root) is forwarded to neurohealhaven.com.
 * - On neurohealhaven.com: any umbrella route (/library, /insights/*, /about,
 *   /media, /masterclass) is forwarded to venusglowhaven.com.
 * - The root `/` is handled by HomeDispatcher (renders Landing on app host,
 *   VenusHome elsewhere) so we never redirect it.
 * - On preview/dev hosts this is a complete no-op.
 */
export const DomainRedirect = () => {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') return; // dispatcher handles root

    if (isVenusGlowHost() && !isUmbrellaPath(path)) {
      window.location.replace(`${NEUROHEAL_ORIGIN}${path}${location.search}${location.hash}`);
      return;
    }
    if (isNeuroHealHost() && isUmbrellaPath(path)) {
      window.location.replace(`${VENUS_ORIGIN}${path}${location.search}${location.hash}`);
    }
    // NEUROHEAL_ORIGIN, VENUS_ORIGIN, isVenusGlowHost, isNeuroHealHost and
    // isUmbrellaPath are module-level constants/functions — stable identities,
    // safe to omit from the deps array.
  }, [location.pathname, location.search, location.hash]);
  return null;
};

export const NEUROHEAL_APP_ORIGIN = NEUROHEAL_ORIGIN;
export const VENUS_UMBRELLA_ORIGIN = VENUS_ORIGIN;
